import { baseUrl as _baseUrl, isPlain } from "./common/url.js";
import { toPackageTarget } from "./install/package.js";
import TraceMap from './trace/tracemap.js';
// @ts-ignore
import { clearCache as clearFetchCache, fetch as _fetch } from '#fetch';
import { createLogger } from './common/log.js';
import { Resolver } from "./trace/resolver.js";
import { JspmError } from "./common/err.js";
import { analyzeHtml } from "./html/analyze.js";
import { SemverRange } from 'sver';
import { Replacer } from "./common/str.js";
import { getIntegrity } from "./common/integrity.js";
export { analyzeHtml };
/**
 * Supports clearing the global fetch cache in Node.js.
 *
 * Example:
 *
 * ```js
 * import { clearCache } from '@jspm/generator';
 * clearCache();
 * ```
 */
export function clearCache() {
    clearFetchCache();
}
export class Generator {
    /**
     * @param options GeneratorOptions
     *
     * For example:
     *
     * ```js
     * const generator = new Generator({
     *   mapUrl: import.meta.url,
     *   inputMap: {
     *     "imports": {
     *       "react": "https://cdn.skypack.dev/react"
     *     }
     *   },
     *   defaultProvider: 'jspm',
     *   providers: {
     *     '@orgscope': 'nodemodules'
     *   },
     *   customProviders: {},
     *   env: ['production', 'browser'],
     *   cache: false,
     * });
     * ```
     */
    constructor({ baseUrl, mapUrl, rootUrl = undefined, inputMap = undefined, env = ['browser', 'development', 'module'], defaultProvider = 'jspm', customProviders = undefined, providers = {}, resolutions = {}, cache = true, stdlib = '@jspm/core', ignore = [], ipfsAPI } = {}) {
        this.finishInstall = null;
        this.installCnt = 0;
        let fetchOpts = undefined;
        if (cache === 'offline')
            fetchOpts = { cache: 'force-cache' };
        else if (!cache)
            fetchOpts = { cache: 'no-store' };
        else
            fetchOpts = {};
        if (ipfsAPI)
            fetchOpts.ipfsAPI = ipfsAPI;
        const { log, logStream } = createLogger();
        const resolver = new Resolver(log, fetchOpts, true);
        if (customProviders) {
            for (const provider of Object.keys(customProviders)) {
                resolver.addCustomProvider(provider, customProviders[provider]);
            }
        }
        this.logStream = logStream;
        if (mapUrl && !baseUrl) {
            mapUrl = typeof mapUrl === 'string' ? new URL(mapUrl, _baseUrl) : mapUrl;
            try {
                baseUrl = new URL('./', mapUrl);
            }
            catch {
                baseUrl = new URL(mapUrl + '/');
            }
        }
        else if (baseUrl && !mapUrl) {
            mapUrl = baseUrl;
        }
        else if (!mapUrl && !baseUrl) {
            baseUrl = mapUrl = _baseUrl;
        }
        this.baseUrl = typeof baseUrl === 'string' ? new URL(baseUrl, _baseUrl) : baseUrl;
        if (!this.baseUrl.pathname.endsWith('/')) {
            this.baseUrl = new URL(this.baseUrl.href);
            this.baseUrl.pathname += '/';
        }
        this.mapUrl = typeof mapUrl === 'string' ? new URL(mapUrl, this.baseUrl) : mapUrl;
        this.rootUrl = typeof rootUrl === 'string' ? new URL(rootUrl, this.baseUrl) : rootUrl || null;
        if (!this.mapUrl.pathname.endsWith('/')) {
            try {
                this.mapUrl = new URL('./', this.mapUrl);
            }
            catch {
                this.mapUrl = new URL(this.mapUrl.href + '/');
            }
        }
        this.traceMap = new TraceMap(this.mapUrl, {
            baseUrl: this.baseUrl,
            stdlib,
            env,
            defaultProvider,
            providers,
            inputMap,
            ignore,
            resolutions
        }, log, resolver);
    }
    /**
     * Trace a module and install all dependencies necessary into the map
     * to support its execution including static and dynamic module imports.
     *
     * @param specifier Import specifier to trace
     * @param parentUrl Parent URL to trace this specifier from
     */
    async traceInstall(specifier, parentUrl) {
        if (typeof parentUrl === 'string')
            parentUrl = new URL(parentUrl);
        let error = false;
        if (this.installCnt++ === 0)
            this.finishInstall = await this.traceMap.startInstall();
        try {
            await this.traceMap.trace(specifier, parentUrl || this.baseUrl);
        }
        catch (e) {
            error = true;
            throw e;
        }
        finally {
            if (--this.installCnt === 0)
                await this.finishInstall(true);
            if (!error)
                return { staticDeps: [...this.traceMap.staticList], dynamicDeps: [...this.traceMap.dynamicList] };
        }
    }
    /**
     * Generate and inject an import map for an HTML file
     *
     * Traces the module scripts of the HTML via traceInstall and install
     * for URL-like specifiers and bare specifiers respectively.
     *
     * Injects the final generated import map returning the injected HTML
     *
     * @param html String
     * @param injectOptions Injection options
     *
     * Injection options are: `htmlUrl`, `preload`, `integrity`, `whitespace`
     * and `esModuleShims`. The default is `{ esModuleShims: true, whitespace: true }`.
     *
     * ES Module shims will be resolved to the latest version against the provider
     *
     * Example:
     *
     * ```js
     *  const outputHtml = await generator.htmlGenerate(`
     *    <!doctype html>
     *    <script type="module">import 'react'</script>
     *  `);
     * ```
     *
     * which outputs:
     *
     * ```
     *   <!doctype html>
     *   <script async src="https://ga.jspm.io/npm:es-module-shims@1.4.1/dist/es-module-shims.js"></script>
     *   <script type="importmap">
     *   {...}
     *   </script>
     *   <script type="module">import 'react'</script>
     * ```
     *
     */
    async htmlGenerate(html, { htmlUrl, preload = false, integrity = false, whitespace = true, esModuleShims = true } = {}) {
        if (typeof htmlUrl === 'string')
            htmlUrl = new URL(htmlUrl);
        if (integrity)
            preload = true;
        const analysis = analyzeHtml(html, htmlUrl);
        let preloadDeps = [];
        // TODO:
        // extract lockfile from map
        await Promise.all([...new Set([...analysis.staticImports, ...analysis.dynamicImports])].map(async (impt) => {
            if (isPlain(impt)) {
                var { staticDeps } = await this.install(impt);
            }
            else {
                var { staticDeps } = await this.traceInstall(impt, analysis.base);
            }
            preloadDeps = preloadDeps.concat(staticDeps);
        }));
        const replacer = new Replacer(html);
        let esms = '';
        if (esModuleShims) {
            const esmsPkg = await this.traceMap.resolver.resolveLatestTarget({ name: 'es-module-shims', registry: 'npm', ranges: [new SemverRange('*')] }, false, this.traceMap.installer.defaultProvider);
            const esmsUrl = this.traceMap.resolver.pkgToUrl(esmsPkg, this.traceMap.installer.defaultProvider) + 'dist/es-module-shims.js';
            esms = `<script async src="${esmsUrl}" crossorigin="anonymous"${integrity ? ` integrity="${await getIntegrity(esmsUrl, this.traceMap.resolver.fetchOpts)}"` : ''}></script>${analysis.map.newlineTab}`;
        }
        if (esModuleShims !== undefined && analysis.esModuleShims) {
            replacer.remove(analysis.esModuleShims.start, analysis.esModuleShims.end, true);
        }
        let preloads = '';
        if (preload && preloadDeps.length) {
            let first = true;
            for (let dep of preloadDeps) {
                if (first || whitespace)
                    preloads += analysis.map.newlineTab;
                if (first)
                    first = false;
                if (integrity) {
                    preloads += `<link rel="modulepreload" href="${dep}" integrity="${await getIntegrity(dep, this.traceMap.resolver.fetchOpts)}" />`;
                }
                else {
                    preloads += `<link rel="modulepreload" href="${dep}" />`;
                }
            }
        }
        if (preload !== undefined) {
            for (const preload of analysis.preloads) {
                replacer.remove(preload.start, preload.end, true);
            }
        }
        replacer.replace(analysis.map.start, analysis.map.end, esms +
            '<script type="importmap">' +
            (whitespace ? '\n' : '') +
            JSON.stringify(this.getMap(), null, whitespace ? 2 : 0) +
            (whitespace ? analysis.map.newlineTab : '') +
            '</script>' +
            preloads +
            (analysis.map.newScript ? analysis.map.newlineTab : ''));
        return replacer.source;
    }
    /**
     * Install a package target into the import map, including all its dependency resolutions via tracing.
     * @param install Package to install
     *
     * For example:
     *
     * ```js
     * // Install a new package into the import map
     * await generator.install('react-dom');
     *
     * // Install a package version and subpath into the import map (installs lit/decorators.js)
     * await generator.install('lit@2/decorators.js');
     *
     * // Install a package version to a custom alias
     * await generator.install({ alias: 'react16', target: 'react@16' });
     *
     * // Install a specific subpath of a package
     * await generator.install({ target: 'lit@2', subpath: './html.js' });
     *
     * // Install an export from a locally located package folder into the map
     * // The package.json is used to determine the exports and dependencies.
     * await generator.install({ alias: 'mypkg', target: './packages/local-pkg', subpath: './feature' });
     * ```
     *
     */
    async install(install) {
        this.traceMap.clearLists();
        if (Array.isArray(install))
            return await Promise.all(install.map(install => this.install(install))).then(() => ({
                staticDeps: [...this.traceMap.staticList],
                dynamicDeps: [...this.traceMap.dynamicList]
            }));
        if (arguments.length !== 1)
            throw new Error('Install takes a single target string or object.');
        if (typeof install !== 'string' && install.subpaths !== undefined) {
            install.subpaths.every(subpath => {
                if (typeof subpath !== 'string' || (subpath !== '.' && !subpath.startsWith('./')))
                    throw new Error(`Install subpath "${subpath}" must be equal to "." or start with "./".`);
            });
            return await Promise.all(install.subpaths.map(subpath => this.install({
                target: install.target,
                alias: install.alias,
                subpath
            }))).then(() => ({ staticDeps: [...this.traceMap.staticList], dynamicDeps: [...this.traceMap.dynamicList] }));
        }
        let error = false;
        if (this.installCnt++ === 0)
            this.finishInstall = await this.traceMap.startInstall();
        try {
            const { alias, target, subpath } = await installToTarget.call(this, install);
            await this.traceMap.add(alias, target);
            await this.traceMap.trace(alias + subpath.slice(1), this.mapUrl);
        }
        catch (e) {
            error = true;
            throw e;
        }
        finally {
            if (--this.installCnt === 0)
                await this.finishInstall(true);
            if (!error)
                return { staticDeps: [...this.traceMap.staticList], dynamicDeps: [...this.traceMap.dynamicList] };
        }
    }
    /**
     * Resolve a specifier using the import map.
     *
     * @param specifier Module to resolve
     * @param parentUrl ParentURL of module to resolve
     * @returns Resolved URL string
     */
    resolve(specifier, parentUrl = this.baseUrl) {
        if (typeof parentUrl === 'string')
            parentUrl = new URL(parentUrl, this.baseUrl);
        const resolved = this.traceMap.map.resolve(specifier, parentUrl);
        if (resolved === null)
            throw new JspmError(`Unable to resolve "${specifier}" from ${parentUrl.href}`, 'MODULE_NOT_FOUND');
        return resolved;
    }
    /**
     * Get the import map JSON
     */
    get importMap() {
        return this.traceMap.map;
    }
    /**
     *
     * @param url
     * @returns
     */
    getAnalysis(url) {
        if (typeof url !== 'string')
            url = url.href;
        const trace = this.traceMap.tracedUrls[url];
        if (!trace)
            throw new Error(`The URL ${url} has not been traced by this generator instance.`);
        return {
            format: trace.format,
            staticDeps: trace.deps,
            dynamicDeps: trace.dynamicDeps,
            cjsLazyDeps: trace.cjsLazyDeps || []
        };
    }
    getMap() {
        const map = this.traceMap.map.clone();
        map.flatten(this.rootUrl ? this.rootUrl : this.baseUrl);
        if (this.rootUrl)
            map.rebase(this.rootUrl.href, true);
        map.sort();
        return map.toJSON();
    }
}
/**
 * _Use the internal fetch implementation, useful for hooking into the same shared local fetch cache._
 *
 * ```js
 * import { fetch } from '@jspm/generator';
 *
 * const res = await fetch(url);
 * console.log(await res.text());
 * ```
 *
 * Use the `{ cache: 'no-store' }` option to disable the cache, and the `{ cache: 'force-cache' }` option to enforce the offline cache.
 */
export async function fetch(url, opts = {}) {
    // @ts-ignore
    return _fetch(url, opts);
}
/**
 * Get the lookup resolution information for a specific install.
 *
 * @param install The install object
 * @param lookupOptions Provider and cache defaults for lookup
 * @returns The resolved install and exact package { install, resolved }
 */
export async function lookup(install, { provider, cache } = {}) {
    const generator = new Generator({ cache: !cache, defaultProvider: provider });
    const { target, subpath, alias } = await installToTarget.call(generator, install);
    if (target instanceof URL)
        throw new Error('URL lookups not supported');
    const resolved = await generator.traceMap.resolver.resolveLatestTarget(target, true, generator.traceMap.installer.defaultProvider);
    return {
        install: {
            target: {
                registry: target.registry,
                name: target.name,
                range: target.ranges.map(range => range.toString()).join(' || ')
            },
            subpath,
            alias
        },
        resolved
    };
}
/**
 * Get the package.json configuration for a specific URL or package.
 *
 * @param pkg Package to lookup configuration for
 * @param lookupOptions Optional provider and cache defaults for lookup
 * @returns Package JSON configuration
 *
 * Example:
 * ```js
 * import { getPackageConfig } from '@jspm/generator';
 *
 * // Supports a resolved package
 * {
 *   const packageJson = await getPackageConfig({ registry: 'npm', name: 'lit-element', version: '2.5.1' });
 * }
 *
 * // Or alternatively provide any URL
 * {
 *   const packageJson = await getPackageConfig('https://ga.jspm.io/npm:lit-element@2.5.1/lit-element.js');
 * }
 * ```
 */
export async function getPackageConfig(pkg, { provider, cache } = {}) {
    const generator = new Generator({ cache: !cache, defaultProvider: provider });
    if (typeof pkg === 'object' && 'name' in pkg)
        pkg = generator.traceMap.resolver.pkgToUrl(pkg, generator.traceMap.installer.defaultProvider);
    else if (typeof pkg === 'string')
        pkg = new URL(pkg).href;
    else
        pkg = pkg.href;
    return generator.traceMap.resolver.getPackageConfig(pkg);
}
/**
 * Get the package base URL for the given module URL.
 *
 * @param url module URL
 * @param lookupOptions Optional provider and cache defaults for lookup
 * @returns Base package URL
 *
 * Modules can be remote CDN URLs or local file:/// URLs.
 *
 * All modules in JSPM are resolved as within a package boundary, which is the
 * parent path of the package containing a package.json file.
 *
 * For JSPM CDN this will always be the base of the package as defined by the
 * JSPM CDN provider. For non-provider-defined origins it is always determined
 * by trying to fetch the package.json in each parent path until the root is reached
 * or one is found. On file:/// URLs this exactly matches the Node.js resolution
 * algorithm boundary lookup.
 *
 * This package.json file controls the package name, imports resolution, dependency
 * resolutions and other package information.
 *
 * getPackageBase will return the folder containing the package.json,
 * with a trailing '/'.
 *
 * This URL will either be the root URL of the origin, or it will be a
 * path "pkgBase" such that fetch(`${pkgBase}package.json`) is an existing
 * package.json file.
 *
 * For example:
 *
 * ```js
 *   import { getPackageBase } from '@jspm/generator';
 *   const pkgUrl = await getPackageBase('https://ga.jspm.io/npm:lit-element@2.5.1/lit-element.js');
 *   // Returns: https://ga.jspm.io/npm:lit-element@2.5.1/
 * ```
 */
export async function getPackageBase(url, { provider, cache } = {}) {
    const generator = new Generator({ cache: !cache, defaultProvider: provider });
    return generator.traceMap.resolver.getPackageBase(typeof url === 'string' ? url : url.href);
}
async function installToTarget(install) {
    if (typeof install === 'string')
        install = { target: install };
    if (typeof install.target !== 'string')
        throw new Error('All installs require a "target" string.');
    if (install.subpath !== undefined && (typeof install.subpath !== 'string' || (install.subpath !== '.' && !install.subpath.startsWith('./'))))
        throw new Error(`Install subpath "${install.subpath}" must be a string equal to "." or starting with "./".${typeof install.subpath === 'string' ? `\nTry setting the subpath to "./${install.subpath}"` : ''}`);
    const { alias, target, subpath } = await toPackageTarget(this.traceMap.resolver, install.target, this.baseUrl.href);
    return {
        alias: install.alias || alias,
        target,
        subpath: install.subpath || subpath
    };
}
//# sourceMappingURL=generator.js.map